/**
 * SourceSelectionStep Component
 *
 * Step 2: Choose between document upload, guided questions, or free text.
 */

import { SourceType, WizardState } from './types';

interface SourceSelectionStepProps {
  state: WizardState;
  onChange: (updates: Partial<WizardState>) => void;
}

interface SourceOption {
  type: SourceType;
  title: string;
  description: string;
  icon: string;
  recommended?: boolean;
}

const SOURCE_OPTIONS: SourceOption[] = [
  {
    type: 'document',
    title: 'Upload Materials',
    description: 'Upload PDF or DOCX training documents. AI will extract key content automatically.',
    icon: 'lucide:file-up',
  },
  {
    type: 'guided',
    title: 'Guided Questions',
    description: 'Answer 6 focused questions about the skill being assessed. Best for most use cases.',
    icon: 'lucide:message-circle',
    recommended: true,
  },
  {
    type: 'freetext',
    title: 'Free Text',
    description: 'Paste training materials, job descriptions, or other context directly.',
    icon: 'lucide:file-text',
  },
];

export function SourceSelectionStep({ state, onChange }: SourceSelectionStepProps) {
  const handleSelect = (sourceType: SourceType) => {
    // Clear fields from other paths when switching
    const updates: Partial<WizardState> = { sourceType };

    if (sourceType !== 'document') {
      updates.documentText = '';
      updates.documentFileName = '';
    }
    if (sourceType !== 'guided') {
      updates.guidedAnswers = {};
    }
    if (sourceType !== 'freetext') {
      updates.additionalContext = '';
    }

    onChange(updates);
  };

  return (
    <div>
      <h2 style={{ marginBottom: 8 }}>Content Source</h2>
      <p className="text-muted" style={{ marginBottom: 24 }}>
        How would you like to provide context for the AI to generate your assessment package?
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {SOURCE_OPTIONS.map((option) => {
          const isSelected = state.sourceType === option.type;

          return (
            <button
              key={option.type}
              type="button"
              onClick={() => handleSelect(option.type)}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 16,
                padding: 20,
                background: isSelected ? 'var(--accent-primary-light)' : 'var(--bg-muted)',
                border: isSelected
                  ? '2px solid var(--accent-primary)'
                  : '2px solid transparent',
                borderRadius: 8,
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'all 0.2s',
              }}
              onMouseOver={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.borderColor = 'var(--border-hover)';
                }
              }}
              onMouseOut={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.borderColor = 'transparent';
                }
              }}
            >
              {/* Icon */}
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 8,
                  background: isSelected ? 'var(--accent-primary)' : 'var(--bg-secondary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  color: isSelected ? '#fff' : 'var(--text-secondary)',
                }}
              >
                <iconify-icon icon={option.icon} width="20" height="20"></iconify-icon>
              </div>

              {/* Content */}
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span
                    style={{
                      fontSize: 15,
                      fontWeight: 600,
                      color: isSelected ? 'var(--accent-primary)' : 'var(--text-primary)',
                    }}
                  >
                    {option.title}
                  </span>
                  {option.recommended && (
                    <span
                      style={{
                        fontSize: 11,
                        padding: '2px 6px',
                        background: 'var(--semantic-success)',
                        color: '#fff',
                        borderRadius: 4,
                        fontWeight: 500,
                      }}
                    >
                      Recommended
                    </span>
                  )}
                </div>
                <p
                  style={{
                    margin: 0,
                    fontSize: 13,
                    color: 'var(--text-secondary)',
                    lineHeight: 1.5,
                  }}
                >
                  {option.description}
                </p>
              </div>

              {/* Selection indicator */}
              <div
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  border: isSelected
                    ? '6px solid var(--accent-primary)'
                    : '2px solid var(--border-default)',
                  flexShrink: 0,
                  marginTop: 4,
                }}
              />
            </button>
          );
        })}
      </div>
    </div>
  );
}
